import React, { useState, lazy } from 'react';
import { Check, Gift, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Features() {
  const [openTomo, setOpenTomo] = useState<number>(0);
  const tomos = [{
    num: 'T1',
    title: 'Formación del Contrato',
    subtitle: 'La base que el profe siempre pregunta',
    desc: 'Entendé qué es un contrato, cómo se forma y cuándo existe legalmente. Dominá la oferta, la aceptación y el sistema de la recepción del art. 980 CCCN. El tomo que resuelve el 40% del parcial.',
    bullets: ['Oferta, aceptación y perfeccionamiento', 'Los 3 elementos esenciales del contrato', 'Todas las clasificaciones con ejemplos argentinos']
  }, {
    num: 'T2',
    title: 'Obligaciones',
    subtitle: 'El corazón del Derecho Civil',
    desc: 'Todo lo que necesitás saber sobre obligaciones, elementos, clasificaciones y medios de extinción explicados de forma simple.',
    bullets: ['Elementos esenciales y accidentales', 'Clasificación completa', 'Pago y otros medios extintivos']
  }, {
    num: 'T3',
    title: 'Efectos del Contrato y Vicios',
    subtitle: 'Error, dolo, violencia y lesión',
    desc: 'Comprendé los efectos entre partes y terceros, y dominá los vicios que pueden afectar la validez del acto jurídico.',
    bullets: ['Efectos relativos y absolutos', 'Vicios de la voluntad', 'Vicios de los actos jurídicos']
  }, {
    num: 'T4',
    title: 'Nulidades',
    subtitle: 'Cuándo el contrato no vale',
    desc: 'El sistema de nulidades del CCCN explicado paso a paso para que no te confundas nunca más en un caso práctico.',
    bullets: ['Nulidad absoluta vs relativa', 'Nulidad total vs parcial', 'Efectos de la nulidad']
  }, {
    num: 'T5',
    title: 'Contratos Típicos',
    subtitle: 'Los que más pregunta el profesor',
    desc: 'Análisis detallado de los contratos en particular más tomados en los parciales de todo el país.',
    bullets: ['Compraventa y Locación', 'Mandato y Donación', 'Fianza y Mutuo']
  }, {
    num: 'T6',
    title: 'Estrategia de Examen',
    subtitle: 'Cómo aprobar Contratos de una vez',
    desc: 'Técnicas probadas para rendir parciales escritos y orales de Contratos sin bloquearte.',
    bullets: ['Cómo resolver casos prácticos', 'Estructura de respuesta oral', 'Gestión del tiempo en el examen']
  }];
  const bonuses = [{
    id: 'B1',
    title: 'Cronograma de Estudio Personalizable',
    desc: 'Plan de 30, 15 y 7 días de emergencia. Completá la fecha de tu parcial y el sistema te dice exactamente qué estudiar cada día.',
    img: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=400&q=55'
  }, {
    id: 'B2',
    title: 'Fichas de los 50 Artículos más Importantes',
    desc: '50 fichas en formato tarjeta con artículo, definición en lenguaje simple, cuándo aplica y ejemplo argentino. Diseñadas para imprimir.',
    img: 'https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&w=400&q=55'
  }, {
    id: 'B3',
    title: 'Guía Completa para Rendir el Oral',
    desc: '20 slides con el método de respuesta oral, las 20 preguntas más frecuentes con respuesta modelo y qué hacer cuando te quedás en blanco.',
    img: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=400&q=55'
  }, {
    id: 'B4',
    title: 'Mapa Mental Completo de Toda la Materia',
    desc: 'Toda la materia en un esquema visual formato A3 imprimible. Los 5 tomos conectados en una sola imagen.',
    img: 'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?auto=format&fit=crop&w=400&q=55'
  }, {
    id: 'B5',
    title: '20 Casos Prácticos Extra con Resolución',
    desc: '20 casos ordenados de menor a mayor dificultad, con resolución paso a paso y artículos del CCCN. Nivel básico, medio y final.',
    img: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=400&q=55'
  }, {
    id: 'B6',
    title: 'Glosario de Latín Jurídico',
    desc: '60 términos latinos que aparecen en los libros, en clase y en el oral. Con traducción literal, significado jurídico y ejemplo de uso.',
    img: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=400&q=55'
  }];
  return <section className="py-12 md:py-20 bg-slate-50" data-id="element-48">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-49">
        {/* System Overview */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12 md:mb-20" data-id="element-50">
          <span className="text-brand-600 font-bold tracking-wider uppercase text-xs md:text-sm mb-2 block" data-id="element-51">
            SISTEMA N°1 EN ARGENTINA
          </span>
          <h2 className="text-2xl md:text-5xl font-extrabold text-slate-900 mb-3 md:mb-6" data-id="element-52">
            Saber Click
          </h2>
          <p className="text-base md:text-xl text-slate-600 mb-6 md:mb-10" data-id="element-53">
            Sistema completo · 6 Tomos + 6 Bonus · Para estudiantes de Abogacía
            · Todo resuelto.
          </p>

          <div className="flex flex-wrap justify-center gap-2 md:gap-3 max-w-3xl mx-auto" data-id="element-54">
            {['+261 slides', '20+ casos prácticos', 'Teoría resumida', 'Artículos del CCCN', 'Estrategia de examen', 'Guía de oral', 'Mapa mental', 'Simulacros', 'Plan de estudio', 'Autoevaluadores', 'Fichas de repaso', 'Glosario jurídico', 'Cronograma'].map((tag, i) => <motion.span key={i} whileHover={{
            scale: 1.05
          }} className="px-3 py-1.5 md:px-4 md:py-2 bg-white border border-slate-200 rounded-full text-xs md:text-sm font-medium text-slate-700 shadow-sm cursor-default" data-id="element-55">
                {tag}
              </motion.span>)}
          </div>
          <p className="mt-6 md:mt-8 text-sm md:text-base text-slate-500 italic" data-id="element-56">
            Funciona desde hoy en tu casa, en el colectivo o la noche antes del
            parcial.
          </p>
        </motion.div>

        {/* Tomos */}
        <div className="mb-16 md:mb-24" data-id="element-57">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center mb-8 md:mb-12" data-id="element-58">
            <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-2 md:mb-4" data-id="element-59">
              CONTENIDO COMPLETO LOS 6 TOMOS
            </h2>
            <p className="text-base md:text-xl text-slate-600" data-id="element-60">
              Tu hoja de ruta para dominar Contratos y Obligaciones
            </p>
          </motion.div>

          <div className="space-y-3 md:space-y-4" data-id="element-61">
            {tomos.map((tomo, idx) => {
            const isOpen = openTomo === idx;
            return <motion.div key={idx} initial={false} animate={{
              backgroundColor: isOpen ? '#ffffff' : '#f8fafc'
            }} className={`rounded-2xl border ${isOpen ? 'border-brand-300 shadow-md' : 'border-slate-200 shadow-sm'} overflow-hidden transition-colors`} data-id="element-62">
                  <button onClick={() => setOpenTomo(isOpen ? -1 : idx)} className="w-full p-4 md:p-6 flex items-center gap-4 md:gap-6 text-left focus:outline-none" data-id="element-63">
                    <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl flex items-center justify-center flex-shrink-0 border transition-colors ${isOpen ? 'bg-brand-100 border-brand-200' : 'bg-white border-slate-200'}`} data-id="element-64">
                      <span className={`text-xl md:text-2xl font-black ${isOpen ? 'text-brand-700' : 'text-slate-500'}`} data-id="element-65">
                        {tomo.num}
                      </span>
                    </div>
                    <div className="flex-grow" data-id="element-66">
                      <h3 className="text-lg md:text-2xl font-bold text-slate-900 mb-0.5 md:mb-1" data-id="element-67">
                        {tomo.title}
                      </h3>
                      <p className="text-sm md:text-base text-brand-600 font-medium" data-id="element-68">
                        {tomo.subtitle}
                      </p>
                    </div>
                    <motion.div animate={{
                  rotate: isOpen ? 180 : 0
                }} className="flex-shrink-0 text-slate-400" data-id="element-69">
                      <ChevronDown className="w-5 h-5 md:w-6 md:h-6" data-id="element-70" />
                    </motion.div>
                  </button>

                  <AnimatePresence initial={false} data-id="element-71">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-72">
                        <div className="px-4 pb-4 md:px-6 md:pb-6 pt-0" data-id="element-73">
                          <div className="pt-4 border-t border-slate-100 ml-16 md:ml-22" data-id="element-74">
                            <p className="text-sm md:text-base text-slate-600 mb-4" data-id="element-75">
                              {tomo.desc}
                            </p>
                            <ul className="space-y-2" data-id="element-76">
                              {tomo.bullets?.map((bullet, i) => <li key={i} className="flex items-start gap-2 text-xs md:text-sm text-slate-700" data-id="element-77">
                                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" data-id="element-78" />
                                  {bullet}
                                </li>)}
                            </ul>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Bonuses */}
        <div data-id="element-79">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="text-center mb-8 md:mb-12" data-id="element-80">
            <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent-100 mb-3 md:mb-4" data-id="element-81">
              <Gift className="w-6 h-6 md:w-8 md:h-8 text-accent-600" data-id="element-82" />
            </div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-2 md:mb-4" data-id="element-83">
              INCLUIDOS VERSION PRO
            </h2>
            <p className="text-base md:text-xl text-slate-600" data-id="element-84">
              6 Bonus Estratégicos. Sin costo extra
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5" data-id="element-85">
            {bonuses.map((bonus, idx) => <motion.div key={idx} whileHover={{
            y: -4
          }} className="bg-white rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-brand-300 hover:shadow-md transition-all flex flex-col" data-id="element-86">
                {/* Representative image */}
                <div className="relative aspect-[16/9] overflow-hidden bg-slate-100" data-id="element-87">
                  <img src={bonus.img} alt={bonus.title} width={400} height={225} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-88" />
                  <div className="absolute top-2 right-2 bg-accent-500 text-white text-[10px] md:text-xs font-bold px-2 py-0.5 md:py-1 rounded-full shadow" data-id="element-89">
                    GRATIS
                  </div>
                  <span className="absolute bottom-2 left-2 font-black text-white/90 text-sm md:text-base drop-shadow" data-id="element-90">
                    {bonus.id}
                  </span>
                </div>

                <div className="p-3 md:p-4 flex-grow" data-id="element-91">
                  <h3 className="font-bold text-xs md:text-base text-slate-900 mb-1 md:mb-1.5 leading-tight" data-id="element-92">
                    {bonus.title}
                  </h3>
                  <p className="text-[11px] md:text-sm text-slate-600 leading-snug" data-id="element-93">
                    {bonus.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="mt-8 md:mt-10 text-center" data-id="element-94">
            <p className="text-base md:text-lg font-medium text-slate-500 line-through mb-1" data-id="element-95">
              VALOR TOTAL: $48.000
            </p>
            <p className="text-xl md:text-2xl font-black text-accent-600" data-id="element-96">
              HOY GRATIS CON TU PACK
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}